import { create } from 'zustand';
import { useMemo } from 'react';
import { subscribeWithSelector } from 'zustand/middleware';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { authService } from '@/services/auth';
import { arenaRealtimeService } from '@/services/realtime';
import {
  GameSession,
  SessionControlAction,
  SessionStats,
  SessionCreationData
} from '@/types';
import type { Player, GrenadeThrowEvent, NetProjectile, GrenadeProjectile } from '@/types';
import logger from '@/utils/logger';

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const hasToDate = (value: unknown): value is { toDate: () => Date } =>
  typeof value === 'object' &&
  value !== null &&
  'toDate' in value &&
  typeof (value as { toDate?: unknown }).toDate === 'function';

const hasSeconds = (value: unknown): value is { seconds: number } =>
  typeof value === 'object' &&
  value !== null &&
  'seconds' in value &&
  typeof (value as { seconds?: unknown }).seconds === 'number';

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toBoolean = (value: unknown): boolean => Boolean(value);

const toOptionalNumber = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

interface GameSessionState {
  // Session data
  sessions: GameSession[];
  currentSession: GameSession | null;
  selectedSessionId: string | null;
  sessionStats: SessionStats | null;

  // Real-time subscription
  unsubscribeSession: (() => void) | null;

  // UI state
  isLoading: boolean;
  isCreatingSession: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // Actions - Session Management
  loadSessions: (teacherId: string) => Promise<void>;
  createSession: (sessionData: SessionCreationData, teacherId: string) => Promise<{ sessionId: string; gameCode: string }>;
  selectSession: (sessionId: string) => Promise<void>;
  updateSession: (sessionId: string, updates: Partial<GameSession>) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  refreshSession: (sessionId: string) => Promise<void>;

  // Actions - Session Control
  executeControl: (sessionId: string, action: Omit<SessionControlAction, 'timestamp'>) => Promise<void>;
  startGame: (sessionId: string) => Promise<void>;
  pauseGame: (sessionId: string) => Promise<void>;
  resumeGame: (sessionId: string) => Promise<void>;
  nextQuestion: (sessionId: string) => Promise<void>;
  endGame: (sessionId: string) => Promise<void>;
  kickPlayer: (sessionId: string, playerId: string) => Promise<void>;
  advancePhase: (sessionId: string) => Promise<void>;
  proceedAfterScoring: (sessionId: string) => Promise<void>;
  transitionToLeaderboard: (sessionId: string) => Promise<void>;
  skipBonusPhase: (sessionId: string) => Promise<void>;

  // New teacher game control actions
  startGamePhase: (sessionId: string) => Promise<void>;
  advanceToNextPhase: (sessionId: string) => Promise<void>;

  // Actions - Real-time Data
  loadSessionStats: (sessionId: string) => Promise<void>;
  subscribeToSession: (sessionId: string) => () => void;
  unsubscribeFromSession: () => void;
  setCurrentSessionDirect: (session: GameSession) => void;

  // Actions - Utility
  clearError: () => void;
  clearSelectedSession: () => void;
  reset: () => void;
}

const initialState = {
  sessions: [],
  currentSession: null,
  selectedSessionId: null,
  sessionStats: null,
  unsubscribeSession: null,
  isLoading: false,
  isCreatingSession: false,
  error: null,
  lastUpdated: null,
};

const upsertSession = (sessions: GameSession[], session: GameSession): GameSession[] => {
  const existingIndex = sessions.findIndex(existingSession => existingSession.id === session.id);

  if (existingIndex === -1) {
    return [session, ...sessions];
  }

  return sessions.map(existingSession =>
    existingSession.id === session.id ? session : existingSession
  );
};

export const useGameSessionStore = create<GameSessionState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Session Management Actions
    loadSessions: async (teacherId: string) => {
      set({ isLoading: true, error: null });
      try {
        const sessions = await authService.getTeacherGameSessions(teacherId);

        set({
          sessions,
          isLoading: false,
          lastUpdated: new Date(),
          error: null
        });
      } catch (error: unknown) {
        logger.error('Failed to load sessions:', error);
        set({
          error: getErrorMessage(error),
          isLoading: false,
          sessions: []
        });
      }
    },

    createSession: async (sessionData: SessionCreationData, teacherId: string): Promise<{ sessionId: string; gameCode: string }> => {
      set({ isCreatingSession: true, error: null });
      try {
        const newSession = await authService.createGameSession(sessionData, teacherId);

        // Add the new session to the list
        const currentSessions = get().sessions;
        set({
          sessions: [newSession, ...currentSessions],
          isCreatingSession: false,
          lastUpdated: new Date(),
          error: null
        });

        return { sessionId: newSession.id, gameCode: newSession.gameCode };
      } catch (error: unknown) {
        logger.error('Failed to create session:', error);
        set({
          error: getErrorMessage(error),
          isCreatingSession: false
        });
        throw error;
      }
    },

    selectSession: async (sessionId: string) => {
      set({ isLoading: true, error: null });
      try {
        const session = await authService.getGameSessionById(sessionId);

        if (session) {
          set({
            currentSession: session,
            selectedSessionId: sessionId,
            isLoading: false,
            error: null
          });

          // Load stats for the selected session
          await get().loadSessionStats(sessionId);
        } else {
          throw new Error('Session not found');
        }
      } catch (error: unknown) {
        logger.error('Failed to select session:', error);
        set({
          error: getErrorMessage(error),
          isLoading: false,
          currentSession: null,
          selectedSessionId: null
        });
      }
    },

    updateSession: async (sessionId: string, updates: Partial<GameSession>) => {
      set({ error: null });
      try {
        await authService.updateGameSession(sessionId, updates);

        // Update the session in the local state
        const sessions = get().sessions.map(session =>
          session.id === sessionId
            ? { ...session, ...updates, updatedAt: new Date() }
            : session
        );

        // Update current session if it's the one being updated
        const currentSession = get().currentSession;
        const updatedCurrentSession = currentSession?.id === sessionId
          ? { ...currentSession, ...updates, updatedAt: new Date() }
          : currentSession;

        set({
          sessions,
          currentSession: updatedCurrentSession,
          lastUpdated: new Date()
        });
      } catch (error: unknown) {
        logger.error('Failed to update session:', error);
        set({ error: getErrorMessage(error) });
        throw error;
      }
    },

    deleteSession: async (sessionId: string) => {
      set({ error: null });
      try {
        await authService.deleteGameSession(sessionId);

        // Remove the session from the local state
        const sessions = get().sessions.filter(session => session.id !== sessionId);

        // Clear current session if it's the one being deleted
        const currentSession = get().currentSession;
        const updatedCurrentSession = currentSession?.id === sessionId ? null : currentSession;
        const selectedSessionId = get().selectedSessionId === sessionId ? null : get().selectedSessionId;

        set({
          sessions,
          currentSession: updatedCurrentSession,
          selectedSessionId,
          sessionStats: selectedSessionId ? get().sessionStats : null,
          lastUpdated: new Date()
        });
      } catch (error: unknown) {
        logger.error('Failed to delete session:', error);
        set({ error: getErrorMessage(error) });
        throw error;
      }
    },

    refreshSession: async (sessionId: string) => {
      try {
        const session = await authService.getGameSessionById(sessionId);

        if (session) {
          const { sessions: existingSessions, currentSession, selectedSessionId } = get();
          const shouldPromoteCurrentSession =
            currentSession?.id === sessionId ||
            selectedSessionId === sessionId ||
            !currentSession;

          set({
            sessions: upsertSession(existingSessions, session),
            currentSession: shouldPromoteCurrentSession ? session : currentSession,
            selectedSessionId: shouldPromoteCurrentSession ? sessionId : selectedSessionId,
            lastUpdated: new Date()
          });

          // Refresh stats if this is the selected session
          if (get().selectedSessionId === sessionId) {
            await get().loadSessionStats(sessionId);
          }
        }
      } catch (error: unknown) {
        logger.error('Failed to refresh session:', error);
        set({ error: getErrorMessage(error) });
      }
    },

    // Session Control Actions
    executeControl: async (sessionId: string, action: Omit<SessionControlAction, 'timestamp'>) => {
      set({ error: null });
      try {
        const fullAction: SessionControlAction = {
          ...action,
          timestamp: new Date()
        };

        await authService.executeSessionControl(sessionId, fullAction);

        // Refresh the session and stats after control action
        await get().refreshSession(sessionId);
      } catch (error: unknown) {
        logger.error('Failed to execute control action:', error);
        set({ error: getErrorMessage(error) });
        throw error;
      }
    },

    startGame: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'start_game' });
    },

    pauseGame: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'pause_game' });
    },

    resumeGame: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'resume_game' });
    },

    nextQuestion: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'next_question' });
    },

    endGame: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'end_game' });
    },

    kickPlayer: async (sessionId: string, playerId: string) => {
      await get().executeControl(sessionId, { type: 'kick_player', playerId });
    },

    advancePhase: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'advance_phase' });
    },

    proceedAfterScoring: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'proceed' });
    },

    transitionToLeaderboard: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'show_leaderboard' });
    },

    skipBonusPhase: async (sessionId: string) => {
      await get().executeControl(sessionId, { type: 'skip_bonus' });
    },

    // New teacher game control actions
    startGamePhase: async (sessionId: string) => {
      set({ error: null });
      try {
        await authService.startGamePhase(sessionId);

        // Refresh the session and stats after starting the game
        await get().refreshSession(sessionId);
      } catch (error: unknown) {
        logger.error('Failed to start game phase:', error);
        set({ error: getErrorMessage(error) });
        throw error;
      }
    },

    advanceToNextPhase: async (sessionId: string) => {
      set({ error: null });
      try {
        // Capture current game state before the transition
        const currentState = get().currentSession?.gameState;

        await authService.advanceToNextPhase(sessionId);

        // RTDB lifecycle: init when entering 'running', cleanup when leaving 'running'
        if (currentState === 'question') {
          // Transitioning question → running: init RTDB session
          const session = get().currentSession;
          if (arenaRealtimeService.isAvailable() && session?.players) {
            arenaRealtimeService.initSession(sessionId, session.players).catch(() => {
              // Init is best-effort
            });
          }
        } else if (currentState === 'running') {
          // Transitioning running → scoring: cleanup RTDB session
          if (arenaRealtimeService.isAvailable()) {
            arenaRealtimeService.cleanupSession(sessionId).catch(() => {
              // Cleanup is best-effort
            });
          }
        }

        // Refresh the session and stats after phase advance
        await get().refreshSession(sessionId);
      } catch (error: unknown) {
        logger.error('Failed to advance to next phase:', error);
        set({ error: getErrorMessage(error) });
        throw error;
      }
    },

    // Real-time Data Actions
    loadSessionStats: async (sessionId: string) => {
      try {
        const stats = await authService.getSessionStats(sessionId);

        set({
          sessionStats: stats,
          lastUpdated: new Date()
        });
      } catch (error: unknown) {
        logger.error('Failed to load session stats:', error);
        set({ error: getErrorMessage(error), sessionStats: null });
      }
    },

    subscribeToSession: (sessionId: string) => {
      // Unsubscribe from any existing subscription
      const existingUnsub = get().unsubscribeSession;
      if (existingUnsub) {
        existingUnsub();
      }

      // Create Firestore real-time listener
      const docRef = doc(db, 'gameSessions', sessionId);
      const unsubscribe = onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();

            // Convert Firestore Timestamps to Dates (similar to FirebaseAuthService)
            const convertTimestamp = (timestamp: unknown): Date => {
              if (timestamp instanceof Date) {
                return timestamp;
              }
              if (hasToDate(timestamp)) {
                try {
                  return timestamp.toDate();
                } catch (conversionError) {
                  logger.debug('Failed to convert timestamp via toDate', conversionError);
                }
              }
              if (hasSeconds(timestamp)) {
                return new Date(timestamp.seconds * 1000);
              }
              if (typeof timestamp === 'number') {
                return new Date(timestamp);
              }
              return new Date();
            };

            const convertTimestampToMs = (timestamp: unknown): number => {
              if (typeof timestamp === 'number') {
                return timestamp;
              }
              if (timestamp instanceof Date) {
                return timestamp.getTime();
              }
              if (hasToDate(timestamp)) {
                return timestamp.toDate().getTime();
              }
              if (hasSeconds(timestamp)) {
                return timestamp.seconds * 1000;
              }
              return Date.now();
            };

            const normalizeNetProjectiles = (nets: unknown[]): NetProjectile[] => {
              if (!Array.isArray(nets)) {
                logger.debug('No nets in session data');
                return [];
              }

              logger.debug('Normalizing', nets.length, 'nets from Firebase');

              return nets.map(net => {
                if (!net || typeof net !== 'object') {
                  return null;
                }

                const netObj = net as Record<string, unknown>;
                const startPos = netObj.startPosition as Record<string, unknown> | undefined;
                const currentPos = netObj.currentPosition as Record<string, unknown> | undefined;
                const targetPos = netObj.targetPosition as Record<string, unknown> | undefined;

                const normalized: NetProjectile = {
                  id: String(netObj.id ?? ''),
                  thrownBy: String(netObj.thrownBy ?? ''),
                  startPosition: { x: toNumber(startPos?.x, 0), y: toNumber(startPos?.y, 0) },
                  currentPosition: { x: toNumber(currentPos?.x, 0), y: toNumber(currentPos?.y, 0) },
                  targetPosition: { x: toNumber(targetPos?.x, 0), y: toNumber(targetPos?.y, 0) },
                  createdAt: convertTimestampToMs(netObj.createdAt),
                  isActive: toBoolean(netObj.isActive)
                };

                if (netObj.deactivatedAt) {
                  normalized.deactivatedAt = convertTimestampToMs(netObj.deactivatedAt);
                }

                if (!normalized.id || Number.isNaN(normalized.createdAt)) {
                  return null;
                }

                logger.debug('Normalized net:', normalized.id, normalized);
                return normalized;
              }).filter((net): net is NetProjectile => Boolean(net));
            };

            const normalizeGrenadeProjectiles = (grenades: unknown[]): GrenadeProjectile[] => {
              if (!Array.isArray(grenades)) {
                logger.debug('No grenades in session data');
                return [];
              }

              logger.debug('Normalizing', grenades.length, 'grenades from Firebase');

              return grenades.map(grenade => {
                if (!grenade || typeof grenade !== 'object') {
                  return null;
                }

                const grenadeObj = grenade as Record<string, unknown>;
                const startPos = grenadeObj.startPosition as Record<string, unknown> | undefined;
                const currentPos = grenadeObj.currentPosition as Record<string, unknown> | undefined;
                const velocity = grenadeObj.velocity as Record<string, unknown> | undefined;
                const explosionPos = grenadeObj.explosionPosition as Record<string, unknown> | undefined;

                const normalized: GrenadeProjectile = {
                  id: String(grenadeObj.id ?? ''),
                  thrownBy: String(grenadeObj.thrownBy ?? ''),
                  startPosition: { x: toNumber(startPos?.x, 0), y: toNumber(startPos?.y, 0) },
                  currentPosition: { x: toNumber(currentPos?.x, 0), y: toNumber(currentPos?.y, 0) },
                  velocity: { x: toNumber(velocity?.x, 0), y: toNumber(velocity?.y, 0) },
                  chargeAmount: toNumber(grenadeObj.chargeAmount, 0),
                  throwDistance: toNumber(grenadeObj.throwDistance, 240),
                  createdAt: convertTimestampToMs(grenadeObj.createdAt),
                  isActive: toBoolean(grenadeObj.isActive),
                  hasExploded: toBoolean(grenadeObj.hasExploded),
                  bouncesRemaining: toNumber(grenadeObj.bouncesRemaining, 0)
                };

                if (explosionPos) {
                  normalized.explosionPosition = {
                    x: toNumber(explosionPos.x, 0),
                    y: toNumber(explosionPos.y, 0)
                  };
                }

                if (grenadeObj.deactivatedAt) {
                  normalized.deactivatedAt = convertTimestampToMs(grenadeObj.deactivatedAt);
                }

                if (!normalized.id || Number.isNaN(normalized.createdAt)) {
                  return null;
                }

                logger.debug('Normalized grenade:', normalized.id, normalized);
                return normalized;
              }).filter((grenade): grenade is GrenadeProjectile => Boolean(grenade));
            };

            const normalizePosition = (position: unknown): Player['position'] => {
              if (!position || typeof position !== 'object') {
                return { x: 0, y: 0 };
              }

              const pos = position as Record<string, unknown>;
              const x = toNumber(pos.x ?? pos.lat, 0);
              const y = toNumber(pos.y ?? pos.lng, 0);

              return {
                x: Number.isFinite(x) ? x : 0,
                y: Number.isFinite(y) ? y : 0
              };
            };

            const normalizeNumberArray = (values: unknown): number[] | undefined => {
              if (!Array.isArray(values)) {
                return undefined;
              }
              return values.map(value => toNumber(value, 0));
            };

            const normalizeGrenadeThrowEvent = (event: unknown): GrenadeThrowEvent | undefined => {
              if (!event || typeof event !== 'object') {
                return undefined;
              }

              const eventObj = event as Record<string, unknown>;
              const idValue = eventObj.id;
              const id = typeof idValue === 'string'
                ? idValue
                : idValue != null
                  ? String(idValue)
                  : '';

              if (!id) {
                return undefined;
              }

              const startPosition = normalizePosition(eventObj.startPosition);
              const velocityObj = eventObj.velocity as Record<string, unknown> | undefined;
              const velocityVector = {
                x: toNumber(velocityObj?.x, 0),
                y: toNumber(velocityObj?.y, 0)
              };

              const thrownAt = toNumber(eventObj.thrownAt ?? eventObj.createdAt, Date.now());
              const chargeAmount = toNumber(eventObj.chargeAmount, 0);
              const throwDistance = toNumber(eventObj.throwDistance, 0);
              const bouncesRemaining = toNumber(eventObj.bouncesRemaining, 0);

              return {
                id,
                thrownAt: Number.isFinite(thrownAt) ? thrownAt : Date.now(),
                startPosition,
                velocity: velocityVector,
                chargeAmount: Number.isFinite(chargeAmount) ? chargeAmount : 0,
                throwDistance: Number.isFinite(throwDistance) ? throwDistance : 0,
                bouncesRemaining: Number.isFinite(bouncesRemaining) ? bouncesRemaining : 0
              };
            };

            const normalizePlayers = (players: unknown[]): Player[] => {
              if (!Array.isArray(players)) {
                return [];
              }

              return players.map(rawPlayer => {
                if (!rawPlayer || typeof rawPlayer !== 'object') {
                  return null;
                }

                const rawPlayerObj = rawPlayer as Record<string, unknown>;
                const idValue = rawPlayerObj.id;
                const id = typeof idValue === 'string'
                  ? idValue
                  : idValue != null
                    ? String(idValue)
                    : '';

                if (!id) {
                  return null;
                }

                const normalized: Player = {
                  id,
                  name: typeof rawPlayerObj.name === 'string' ? rawPlayerObj.name : `Player ${id}`,
                  score: toNumber(rawPlayerObj.score, 0) || 0,
                  isReady: toBoolean(rawPlayerObj.isReady),
                  position: normalizePosition(rawPlayerObj.position),
                  items: Array.isArray(rawPlayerObj.items) ? (rawPlayerObj.items as Player['items']) : []
                };

                const characterIndex = toOptionalNumber(rawPlayerObj.characterIndex);
                if (characterIndex !== undefined) {
                  normalized.characterIndex = characterIndex;
                }

                const lastAnswerIndex = toOptionalNumber(rawPlayerObj.lastAnswerIndex);
                if (lastAnswerIndex !== undefined) {
                  normalized.lastAnswerIndex = lastAnswerIndex;
                }

                const roundScores = normalizeNumberArray(rawPlayerObj.roundScores);
                if (roundScores) {
                  normalized.roundScores = roundScores;
                }

                if (rawPlayerObj.currentRoundScore !== undefined) {
                  normalized.currentRoundScore = toNumber(rawPlayerObj.currentRoundScore, 0);
                }

                if (rawPlayerObj.authUid) {
                  normalized.authUid = String(rawPlayerObj.authUid);
                }

                if (rawPlayerObj.netCharges !== undefined) {
                  normalized.netCharges = toNumber(rawPlayerObj.netCharges, 0);
                }

                if (rawPlayerObj.grenadeCharges !== undefined) {
                  normalized.grenadeCharges = toNumber(rawPlayerObj.grenadeCharges, 0);
                }

                if (rawPlayerObj.isImmobilized !== undefined) {
                  normalized.isImmobilized = toBoolean(rawPlayerObj.isImmobilized);
                }

                if (rawPlayerObj.immobilizedUntil) {
                  normalized.immobilizedUntil = convertTimestampToMs(rawPlayerObj.immobilizedUntil);
                }

                if (rawPlayerObj.isBeingPushed !== undefined) {
                  normalized.isBeingPushed = toBoolean(rawPlayerObj.isBeingPushed);
                }

                if (rawPlayerObj.pushVelocity) {
                  const pushVelocity = rawPlayerObj.pushVelocity as Record<string, unknown>;
                  normalized.pushVelocity = {
                    x: toNumber(pushVelocity.x, 0),
                    y: toNumber(pushVelocity.y, 0)
                  };
                }

                if (rawPlayerObj.pushEndTime) {
                  normalized.pushEndTime = convertTimestampToMs(rawPlayerObj.pushEndTime);
                }

                if (rawPlayerObj.shoesEffectNextRound !== undefined) {
                  normalized.shoesEffectNextRound = toBoolean(rawPlayerObj.shoesEffectNextRound);
                }

                if (rawPlayerObj.turboAvailable !== undefined) {
                  normalized.turboAvailable = toBoolean(rawPlayerObj.turboAvailable);
                }

                if (rawPlayerObj.turboUsed !== undefined) {
                  normalized.turboUsed = toBoolean(rawPlayerObj.turboUsed);
                }

                if (rawPlayerObj.lastGrenadeThrow !== undefined) {
                  const normalizedEvent = normalizeGrenadeThrowEvent(rawPlayerObj.lastGrenadeThrow);
                  if (normalizedEvent) {
                    normalized.lastGrenadeThrow = normalizedEvent;
                  } else if (rawPlayerObj.lastGrenadeThrow === null) {
                    normalized.lastGrenadeThrow = null;
                  }
                }

                return normalized;
              }).filter((player): player is Player => Boolean(player?.id));
            };

            // Overlay playerPositions map onto normalized players for contention-free position sync
            const normalizedPlayers = normalizePlayers(data.players || []);
            const playerPositions = data.playerPositions as Record<string, { x: number; y: number }> | undefined;
            if (playerPositions && typeof playerPositions === 'object') {
              for (const player of normalizedPlayers) {
                const pos = playerPositions[player.id];
                if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
                  player.position = { x: pos.x, y: pos.y };
                }
              }
            }

            // Reuse previous projectile arrays if unchanged to avoid unnecessary re-renders
            const prevSession = get().currentSession;

            const rawNets = data.activeNetProjectiles || [];
            const prevNets = prevSession?.activeNetProjectiles;
            const netsChanged = !prevNets
              || rawNets.length !== prevNets.length
              || rawNets.some((net: Record<string, unknown>, i: number) => net?.id !== prevNets[i]?.id);
            const activeNetProjectiles = netsChanged
              ? normalizeNetProjectiles(rawNets)
              : prevNets;

            const rawGrenades = data.activeGrenadeProjectiles || [];
            const prevGrenades = prevSession?.activeGrenadeProjectiles;
            const grenadesChanged = !prevGrenades
              || rawGrenades.length !== prevGrenades.length
              || rawGrenades.some((g: Record<string, unknown>, i: number) => g?.id !== prevGrenades[i]?.id);
            const activeGrenadeProjectiles = grenadesChanged
              ? normalizeGrenadeProjectiles(rawGrenades)
              : prevGrenades;

            const session: GameSession = {
              id: snapshot.id,
              ...data,
              players: normalizedPlayers,
              activeNetProjectiles,
              activeGrenadeProjectiles,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
              startedAt: data.startedAt ? convertTimestamp(data.startedAt) : undefined,
              completedAt: data.completedAt ? convertTimestamp(data.completedAt) : undefined,
              currentPhaseStartTime: data.currentPhaseStartTime ? convertTimestamp(data.currentPhaseStartTime) : undefined,
              currentPhaseEndTime: data.currentPhaseEndTime ? convertTimestamp(data.currentPhaseEndTime) : undefined,
            } as GameSession;

            logger.debug('Session update received. Nets:', session.activeNetProjectiles?.length || 0, 'Grenades:', session.activeGrenadeProjectiles?.length || 0);
            set(state => ({
              sessions: upsertSession(state.sessions, session),
              currentSession: session,
              selectedSessionId: sessionId,
              lastUpdated: new Date()
            }));
          } else {
            logger.warn('Session document no longer exists');
            set({ currentSession: null, error: 'Session not found' });
          }
        },
        (error) => {
          logger.error('Session subscription error:', error);
          set({ error: getErrorMessage(error) });
        }
      );

      const cleanup = () => {
        try {
          unsubscribe();
        } finally {
          set({ unsubscribeSession: null });
        }
      };

      // Store unsubscribe function for cleanup
      set({ unsubscribeSession: cleanup });

      return cleanup;
    },

    unsubscribeFromSession: () => {
      const unsub = get().unsubscribeSession;
      if (unsub) {
        unsub();
        set({ unsubscribeSession: null });
      }
    },

    setCurrentSessionDirect: (session: GameSession) => {
      logger.debug('Direct session update:', {
        sessionId: session.id,
        gameState: session.gameState,
        netsCount: session.activeNetProjectiles?.length || 0
      });
      set({
        currentSession: session,
        selectedSessionId: session.id,
        lastUpdated: new Date()
      });
    },

    // Utility Actions
    clearError: () => {
      set({ error: null });
    },

    clearSelectedSession: () => {
      set({
        currentSession: null,
        selectedSessionId: null,
        sessionStats: null
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);

// Selectors for common use cases
export const useGameSessions = () => useGameSessionStore(state => state.sessions);
export const useCurrentSession = () => useGameSessionStore(state => state.currentSession);
export const useSessionStats = () => useGameSessionStore(state => state.sessionStats);
export const useGameSessionLoading = () => useGameSessionStore(state => state.isLoading);
export const useGameSessionError = () => useGameSessionStore(state => state.error);
export const useSessionActions = () => {
  const loadSessions = useGameSessionStore(state => state.loadSessions);
  const createSession = useGameSessionStore(state => state.createSession);
  const selectSession = useGameSessionStore(state => state.selectSession);
  const updateSession = useGameSessionStore(state => state.updateSession);
  const deleteSession = useGameSessionStore(state => state.deleteSession);
  const refreshSession = useGameSessionStore(state => state.refreshSession);
  const executeControl = useGameSessionStore(state => state.executeControl);
  const startGame = useGameSessionStore(state => state.startGame);
  const pauseGame = useGameSessionStore(state => state.pauseGame);
  const resumeGame = useGameSessionStore(state => state.resumeGame);
  const nextQuestion = useGameSessionStore(state => state.nextQuestion);
  const endGame = useGameSessionStore(state => state.endGame);
  const kickPlayer = useGameSessionStore(state => state.kickPlayer);
  const advancePhase = useGameSessionStore(state => state.advancePhase);
  const proceedAfterScoring = useGameSessionStore(state => state.proceedAfterScoring);
  const transitionToLeaderboard = useGameSessionStore(state => state.transitionToLeaderboard);
  const skipBonusPhase = useGameSessionStore(state => state.skipBonusPhase);
  const startGamePhase = useGameSessionStore(state => state.startGamePhase);
  const advanceToNextPhase = useGameSessionStore(state => state.advanceToNextPhase);
  const setCurrentSessionDirect = useGameSessionStore(state => state.setCurrentSessionDirect);
  const subscribeToSession = useGameSessionStore(state => state.subscribeToSession);
  const unsubscribeFromSession = useGameSessionStore(state => state.unsubscribeFromSession);
  const clearError = useGameSessionStore(state => state.clearError);
  const reset = useGameSessionStore(state => state.reset);

  // Memoize to keep a stable reference for React 19's store snapshot checks
  return useMemo(() => ({
    loadSessions,
    createSession,
    selectSession,
    updateSession,
    deleteSession,
    refreshSession,
    executeControl,
    startGame,
    pauseGame,
    resumeGame,
    nextQuestion,
    endGame,
    kickPlayer,
    advancePhase,
    proceedAfterScoring,
    transitionToLeaderboard,
    skipBonusPhase,
    startGamePhase,
    advanceToNextPhase,
    setCurrentSessionDirect,
    subscribeToSession,
    unsubscribeFromSession,
    clearError,
    reset
  }), [
    loadSessions,
    createSession,
    selectSession,
    updateSession,
    deleteSession,
    refreshSession,
    executeControl,
    startGame,
    pauseGame,
    resumeGame,
    nextQuestion,
    endGame,
    kickPlayer,
    advancePhase,
    proceedAfterScoring,
    transitionToLeaderboard,
    skipBonusPhase,
    startGamePhase,
    advanceToNextPhase,
    setCurrentSessionDirect,
    subscribeToSession,
    unsubscribeFromSession,
    clearError,
    reset
  ]);
};
